"use client"
import React, { useEffect, useMemo, useState } from 'react'
import "./page.css"
import Section from '../../Components/Section/Section'
import Download from '../../Components/Download/Download'
import Image from 'next/image'
import { _eventBus } from '../../core/events/EventBus'
import { useRecState } from '../../Orchestrator/Recoils/Events/Recoil'
import { ICmsNetworkMapStationInfoResponse, ICmsNetworkMapStations, StationsListType } from '../../Orchestrator/Types/Index'
import { useOnLanguageChange } from '../../Components/CustomHooks/useOnLanguageChange'
import { evtTree } from '../../Orchestrator/ConstantTree'
import NetworkMapStationCard from '../../Components/NetworkMaps/NetworkMapStationCard/NetworkMapStationCard';
import { useIntl } from 'react-intl'
import Loader from '../../Components/Loader/Loader'
import { Button, Card, Col, Flex, Row, Typography } from 'antd'
import CommonCardSlider from '../../Components/CommonCardSlider/CommonCardSlider'
import { chunkIntoPages, groupNetworkMapStations } from '../../Lib/Utils/stationGrouping'

const STATIONS_PER_PAGE = 3;

const NetworkMapStationPage = () => {
  const [networkMapStations] = useRecState<ICmsNetworkMapStations[]>("networkMapStationResponseAtom");
  const [networkMapStationInfo] = useRecState<ICmsNetworkMapStationInfoResponse | null>("networkMapStationInfoResponseAtom");

  const intl = useIntl();
  const [initialLoadFinished, setInitialLoadFinished] = useState(false);
  const [stopsData] = useRecState<StationsListType>("stopsResponseAtom");

  useEffect(() => {
    return () => {
      _eventBus.emit({
        eventName: "FLOW_CLOSE",
        data: "NetworkMapStationFlow",
      })
    }
  }, []);

  useOnLanguageChange(() => {
    _eventBus.emit({
      eventName: evtTree.Network.Get,
    });
  });

  const moreDetailsClick = (slug: string) => {
    if (slug) {
      _eventBus.emit({
        eventName: evtTree.Network.GetDetails.Click,
        data: { slug }
      });
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoadFinished(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const { stations, shuttles } = useMemo(
    () => groupNetworkMapStations(networkMapStations ?? []),
    [networkMapStations]
  );

  const renderStationCard = (station: ICmsNetworkMapStations) => (
    <Card key={station.id} className='radius-12 w-100' classNames={{ body: 'p-10 pl-18 pr-18' }}>
      <Flex gap={16} align='center'>
        <img
          src="/assets/booking/red-train.svg"
          height={25}
          alt="train-icon"
        />
        <div>
          <Typography.Title level={4} className='fs-14 fw-700 mb-4 lh-normal'>
            {station?.station?.name}
          </Typography.Title>
          <Button type='link' className='btn p-0 fs-14 color-primary fw-500 h-auto' onClick={() => moreDetailsClick(station.slug)}>
            {intl.formatMessage({ id: "moreDetails" })}
            <span>
              <Image
                src="/assets/network_icons/chevron_right.svg"
                width={8}
                height={12}
                alt="arrow"
              />
            </span>
          </Button>
        </div>
      </Flex>
    </Card>
  );

  // Heading and slider share one positioned wrapper so the dots can sit on the
  // heading row (top-right, as on Popular Trips) instead of below the cards,
  // where they read as belonging to the section underneath.
  const renderStationSection = (
    titleId: string,
    entries: ICmsNetworkMapStations[],
    sectionClassName?: string
  ) => (
    <div className={['stations-section', sectionClassName].filter(Boolean).join(' ')}>
      <Typography.Title level={2} className='fs-18 fw-600 mb-10'>
        {intl.formatMessage({ id: titleId })}
      </Typography.Title>
      <CommonCardSlider
        title=""
        items={chunkIntoPages(entries, STATIONS_PER_PAGE)}
        slidesPerPage={1}
        showArrows={false}
        gap={0}
        className='stations-slider'
        noRecordText={intl.formatMessage({ id: "noData" })}
        getKey={(page, index) => page[0]?.id ?? index}
        renderItem={(page) => (
          <Flex vertical gap={14}>
            {page.map(renderStationCard)}
          </Flex>
        )}
      />
    </div>
  );

  return (
    <div className="networks-main mb-50">
      <div className="container">
        <Row gutter={[24, 24]} >
          <Col lg={8} className='w-100'>
            {
              networkMapStations.length === 0 ?
                <>
                  <Typography.Title level={2} className='fs-18 fw-600 mb-10'>
                    {intl.formatMessage({ id: "stations" })}
                  </Typography.Title>
                  {initialLoadFinished ? <div className='text-center'>{intl.formatMessage({ id: "noData" })}</div> : <Loader />}
                </>
                :
                <>
                  {stations.length > 0 && renderStationSection("stations", stations)}
                  {shuttles.length > 0 && renderStationSection("shuttles", shuttles, stations.length > 0 ? 'mt-30' : undefined)}
                </>
            }
          </Col>
          <Col lg={16} className='w-100'>
            {
              networkMapStationInfo?.data?.desktop_map?.url ?
                <img
                  src={networkMapStationInfo?.data?.desktop_map.url}
                  alt="station-map"
                  className='map-image'
                />
                : <Flex justify='center' align='center' className='h-100'>
                  <Typography.Text className='text-center'>{intl.formatMessage({ id: "noImageAvailable" })}</Typography.Text>
                </Flex>
            }
          </Col>
        </Row>
      </div>
    </div >
  )

  // Deprecated: This design is being replaced with a new one.
  return (
    <>
      <div className="networks-main">
        <div className="container">
          <div className="networks-list">

            {networkMapStations?.length > 0 ?
              <>
                {networkMapStations.map((station, idx) => (
                  <NetworkMapStationCard
                    key={idx}
                    stationData={station?.station}
                    onMoreDetails={() => {
                      moreDetailsClick(station.slug)
                    }}
                    stopsData={stopsData}
                  />
                ))}
              </>
              : initialLoadFinished ? <div className='text-center'>{intl.formatMessage({ id: "noData" })}</div> : <Loader />}
          </div>
        </div>
      </div>
      <Section type="trust_builder offers_trust_builder">
        <div className="container">
          <Download />
        </div>
      </Section>
    </>
  )
}

export default NetworkMapStationPage